import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);
const ADMIN_EXPLICIT_PERMISSIONS = new Set([
  'installation:manage',
  'installation:settlement',
]);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (err) {
        localStorage.removeItem('access_token');
        setUser(null);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authService.login(email, password);
      
      // Simple login - just get the token
      localStorage.setItem('access_token', response.access_token);
      const userData = await authService.getCurrentUser();
      setUser(userData);
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      return false;
    }
  };

  const register = async (email, fullName, password) => {
    try {
      setError(null);
      const response = await authService.register(email, fullName, password);
      
      // Registration now requires verification
      if (response.requires_verification) {
        return response;
      }
      
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
      return null;
    }
  };

  const verifyRegistration = async (userId, code) => {
    try {
      setError(null);
      await authService.verifyRegistration(userId, code);
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
      return false;
    }
  };

  const resendCode = async (userId) => {
    try {
      setError(null);
      const response = await authService.resendCode(userId);
      return response;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
      return null;
    }
  };

  const updateUser = async (userData) => {
    try {
      setError(null);
      const updatedUser = await authService.updateCurrentUser(userData);
      setUser(updatedUser);
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Update failed');
      return false;
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      setUser(await authService.getCurrentUser());
    } catch (err) {
      return {};
    }
  }, []);

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
    }
  };

  const clearError = () => {
    setError(null);
  };

  // Role-based helpers
  const hasRole = (roleName) => {
    return user?.roles?.includes(roleName) || false;
  };

  const hasAnyRole = (roleNames) => {
    if (!user?.roles) return false;
    return roleNames.some(role => user.roles.includes(role));
  };

  const isAdmin = () => {
    return hasRole('admin');
  };

  // Check if user has any role assigned
  const hasAnyRoleAssigned = () => {
    return user?.roles && user.roles.length > 0;
  };

  // Permission-based helpers
  const hasPermission = (permissionName) => {
    // Installation management and settlements always require an explicitly
    // assigned permission, including for administrators.
    if (isAdmin() && !ADMIN_EXPLICIT_PERMISSIONS.has(permissionName)) return true;
    return user?.permissions?.includes(permissionName) || false;
  };

  const hasAnyPermission = (permissionNames) => {
    return permissionNames.some(perm => hasPermission(perm));
  };

  const hasAllPermissions = (permissionNames) => {
    return permissionNames.every(perm => hasPermission(perm));
  };

  // Check if user can access a specific tool/module
  const canAccessSales = () => hasAnyPermission(['module:sales', 'meetings:manage', 'meetings:assignable', 'meetings:statistics', 'aluminium_deals:use', 'aluminium_deals:montage', 'aluminium_deals:translation']);
  const canAccessLogistics = () => hasAnyPermission(['module:logistics', 'email_logistics:view', 'email_logistics:manage']);
  const canAccessLogisticsStojaki = () => hasAnyPermission(['email_logistics:view', 'email_logistics:manage']);
  const canAccessHR = () => hasAnyPermission(['hr:view', 'hr:manage_employees']);
  const canAccessTrainings = () => hasPermission('trainings:view');
  const canAccessProduction = () => hasAnyPermission([
    'production:view',
    'production:monitor_orders',
    'production:monitor_schuco',
    'production:monitor_blinds',
    'production:monitor_glass',
    'production:documents',
    'production:other'
  ]);
  const canAccessProductionMonitor = () => hasPermission('production:monitor_orders');
  const canAccessProductionSchucoMonitor = () => hasPermission('production:monitor_schuco');
  const canAccessProductionBlinds = () => hasPermission('production:monitor_blinds');
  const canAccessProductionGlassMonitor = () => hasPermission('production:monitor_glass');
  const canAccessProductionDocuments = () => hasPermission('production:documents');
  const canAccessProductionOther = () => hasPermission('production:other');
  const canAccessMeetingManager = () => hasPermission('meetings:manage');
  const canAccessManagement = () => hasAnyPermission(['management:view', 'reviews:view', 'order_analytics:view', 'tool_analytics:view']);
  const canAccessGoogleReviews = () => hasPermission('reviews:view');
  const canAccessOrderAnalytics = () => hasPermission('order_analytics:view');
  const canAccessToolAnalytics = () => hasPermission('tool_analytics:view');
  const canAccessOrganization = () => hasAnyPermission(['organization:view', 'tasks:use', 'survey:fill', 'survey:view_results', 'survey:manage']);
  const canAccessMontaz = () => hasAnyPermission(['installation:view', 'installation:manage', 'installation:settlement']);
  const canAccessMontazManage = () => hasPermission('installation:manage');
  const canAccessMontazSettlement = () => hasPermission('installation:settlement');
  const hasOnlyMontazView = () => hasPermission('installation:view') && !hasPermission('installation:manage') && !hasPermission('installation:settlement');

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        verifyRegistration,
        resendCode,
        updateUser,
        refreshUser,
        logout,
        clearError,
        isAuthenticated: !!user,
        // Role helpers
        hasRole,
        hasAnyRole,
        hasAnyRoleAssigned,
        isAdmin,
        // Permission helpers
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        // Convenience access checkers
        canAccessSales,
        canAccessLogistics,
        canAccessHR,
        canAccessTrainings,
        canAccessProduction,
        canAccessProductionMonitor,
        canAccessProductionSchucoMonitor,
        canAccessProductionBlinds,
        canAccessProductionGlassMonitor,
        canAccessProductionDocuments,
        canAccessProductionOther,
        canAccessLogisticsStojaki,
        canAccessMeetingManager,
        canAccessManagement,
        canAccessGoogleReviews,
        canAccessOrderAnalytics,
        canAccessToolAnalytics,
        canAccessOrganization,
        canAccessMontaz,
        canAccessMontazManage,
        canAccessMontazSettlement,
        hasOnlyMontazView,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
